import { IPageResponse } from '@webapp/core/interfaces/response.interface';
import { IStatus } from '@webapp/core/interfaces/status.interface';
import { ICoverage, CoveragePayment, IPolicyPayment } from '@webapp/payments';
import { IPersonalAccidentInsured } from '@webapp/quoter//risks/personal-accidents/interfaces/personal-accidents.interface';
import { IRisk, RiskTag, IVehicleInsurable as IRisksVehicleInsurable, IPersonalAccidentsInsurable, ITransportationGoodsInsurable as IRisksTransportationGoodsInsurable } from '@webapp/risks';
import {
  IRiskDescription,
  ITransportationGoodsInsurable,
  IVehicleInsurable,
} from '@webapp/quotes//interfaces/quotes.interface';
import { Status } from '@webapp/ui';
import { IUser } from '@webapp/users/interfaces/users.interface';

export interface IPolicy {
  id: string;
  internalNumber: number;
  emitionDate: Date;
  coverageName: string;
  risk: IRiskDescription;
  startDate: Date;
  endDate: Date;
  status: string;
  createdAt: Date;
  updatedAt: Date;
  client: {
    id: string;
    user: IUser;
    agent: {
      id: string;
      user: IUser;
    };
  };
}

export type IPolicyRow = IPolicy & IStatus;
export interface IPoliciesPage extends Pick<IPageResponse, 'pagination'> {
  data: IPolicy[];
}

export type IPolicyTGInsurable = ITransportationGoodsInsurable & {
  premium: number;
  pricingMode: CoveragePayment;
  activity: string;
  numberOfLifes: number;
  summary: {
    title: string;
    description: string;
    recommended: boolean;
    id: string;
    items: string[];
    subTitle: string;
  };
};

export interface IPolicyPAInsurable {
  valueSubscription: number;
  pricingMode: CoveragePayment;
  activity: string;
  numberOfLifes: number;
  plan: {
    id: string;
    type: string;
    recommend: boolean;
    coverage: {
      id: string;
      name: string;
      insuredCapital: number;
    }[];
  };
  insured: IPersonalAccidentInsured[];
}
export interface IPolicyDetail extends IPolicyRow {
  currency: string;
  operationNumber: number;
  quote: {
    internalNumber: number;
  };
  transportationGoods: IPolicyTGInsurable;
  personalAccident: IPolicyPAInsurable;
  vehicleInsurable: IVehicleInsurable;
  payment: {
    expiresAt: Date;
    lastFour: string;
    id: string;
    type: string;
    number: string;
    method: string;
  };
}



export interface IInsurer {
    id: string;
    code: string;
    legalName: string;
    displayName: string;
    taxId: string;
    logoUrl: string;
    logoOnDarkUrl: string;
    websiteUrl: string;
    ssnRegistrationNumber: string;
    customerServicePhone: string;
    contactEmail: string;
}

export interface IBasePolicy {
    id: string;
    insurer?: IInsurer;
    risk: IRisk;
    user: IUser;
    agent: IUser;
    additionalCoverage: IBasePolicy;
    availableCoverages: ICoverage[];
    payment: IPolicyPayment;
    operationNumber: string;
    badgeStatus: Status;
    badgeText: string;
    coverage: ICoverage;
    currency: string;
    endDate: string;
    startDate: string;
    daysInFavor: number;
    internalNumber: number;
    quoteId: string;
    quoteInternalNumber: number;
    title: string;
    insurable: unknown;
    status: string;
    transactionID: string;
    tabs?: string[];
}

export interface IClassicCarPolicy extends IBasePolicy {
  risk: IRisk & { tag: 'classic_car' };
  insurable: IRisksVehicleInsurable;
}

export interface IMotorBikePolicy extends IBasePolicy {
  risk: IRisk & { tag: 'motor_bike' };
  insurable: IRisksVehicleInsurable;
}

export interface IQuadPolicy extends IBasePolicy {
  risk: IRisk & { tag: 'quad' };
  insurable: IRisksVehicleInsurable;
}

export interface ITransportationGoodsPolicy extends IBasePolicy {
  risk: IRisk & { tag: 'transportation_goods' };
  insurable: IRisksTransportationGoodsInsurable;
}

export interface IPersonalAccidentsPolicy extends IBasePolicy {
  risk: IRisk & { tag: 'personal_accidents' };
  insurable: IPersonalAccidentsInsurable;
}

export type Policy = IClassicCarPolicy | IMotorBikePolicy | ITransportationGoodsPolicy | IQuadPolicy | IPersonalAccidentsPolicy;

export const PolicyTabNames = {
  INFO: 'Resumen',
  INSURED: 'Asegurable',
  ACTIVATIONS: 'Activaciones',
  ENDORSEMENT: 'Endosos',
};

export type { RiskTag };
